// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useState, useEffect, useReducer } from "react";
import { useSelector } from "react-redux";
import Header from "./Header";
import AppCard from "./AppCard";
import { SearchInput } from "src/components/Unifize";
import {
  setIntegrations,
  installIntegration,
  updateIntegration,
  deleteIntegration
} from "src/actions/integrations";
import type { AppIntegration } from "src/types";
import { getCurrentLanguage } from "src/reducers";
import { apps, initialState } from "src/reducers/integrations";
import * as colors from "src/styles/constants/colors";
import * as spacing from "src/styles/constants/spacing";
import {
  PageContainer,
  AppListContainer,
  AppNotFound,
  SearchContainer
} from "./styles";
import { Text, Skeleton, Stack } from "@chakra-ui/react";
import {
  getIntegrations,
  editIntegration,
  removeIntegration
} from "src/api/integrations";
import { toast } from "react-toastify";

const Integrations = () => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [integrations, dispatch] = useReducer(apps, initialState);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const language = useSelector(({ app }) => getCurrentLanguage(app));

  useEffect(() => {
    fetchIntegrations();
  }, []);

  useEffect(() => {
    setItems(integrations);
    setFilteredItems(integrations);
  }, [integrations]);

  const fetchIntegrations = async () => {
    try {
      const response: Array<AppIntegration> = await getIntegrations();
      if (response) {
        dispatch(setIntegrations(response));
        setLoading(false);
      }
    } catch (e) {
      toast.error(i18n.t(k.UNABLE_FETCH_INTEGRATIONS));
      setLoading(false);
      console.error(e);
    }
  };

  const onInstall = (app: string) => {
    dispatch(installIntegration(app));
    const id: ?number = integrations.find(item => item.title === app)?.id;
    const title: ?string = integrations.find(
      item => item.title === app
    )?.displayTitle;
    if (title) {
      toast.success(`${title} integration installed`);
    }
    if (id) {
      handleUpdate({ id, status: false });
    }
  };

  const handleUpdate = async ({
    id,
    status,
    display
  }: {
    id: number,
    status: boolean,
    display?: string
  }) => {
    try {
      const response: AppIntegration = await editIntegration(id, status);
      if (response) {
        dispatch(updateIntegration(id, response));
        if (display) {
          if (status) {
            toast.error(`${display} integration disabled`);
          } else {
            toast.success(`${display} integration enabled`);
          }
        }
      }
    } catch (e) {
      toast.error(i18n.t(k.UPDATE_INTEGRATION_FAILED));
      console.error(e);
    }
  };

  const handleDisconnect = async ({
    id,
    display
  }: {
    id: number,
    display: string
  }) => {
    try {
      const response = await removeIntegration(id);
      if (response) {
        dispatch(deleteIntegration(id));
        toast.error(`${display} integration disconnected`);
      }
    } catch (e) {
      toast.error(i18n.t(k.DISCONNECT_INTEGRATION_FAILED));
      console.error(e);
    }
  };

  const handleSearch = keyword => {
    const filtered: Array<AppIntegration> = items.filter(app =>
      app.title.toLowerCase().includes(keyword.toLowerCase())
    );
    setFilteredItems(filtered);
  };

  return (
    <PageContainer key={language}>
      <Header
        title={i18n.t(k.APPS)}
        description={i18n.t(k.APPS_DESCRIPTION)}
        showAction={false}
      />

      <SearchContainer>
        <SearchInput placeholder={i18n.t(k.SEARCH)} onInput={handleSearch} />
      </SearchContainer>
      <AppListContainer>
        {filteredItems.map(app => (
          <AppCard
            key={app.id}
            id={app.id}
            display={app.displayTitle}
            title={app.title}
            isDisabled={app.disabled}
            isInstalled={app.installed}
            url={app.authorizationUrl}
            isLoading={isLoading}
            onInstall={onInstall}
            onUpdate={handleUpdate}
            onDisconnect={handleDisconnect}
          />
        ))}
        {!isLoading && filteredItems.length === 0 && (
          <AppNotFound>
            <Text textStyle="label">{i18n.t(k.NO_APPS_FOUND)}</Text>
          </AppNotFound>
        )}
      </AppListContainer>
      {isLoading && (
        <Stack p={spacing.space_m}>
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton
              key={index}
              borderRadius="md"
              height="72px"
              startColor={colors.grey28}
              endColor={colors.grey4}
            />
          ))}
        </Stack>
      )}
    </PageContainer>
  );
};

export default Integrations;
